/**
 * UpdatesPage — /dashboard/system/updates
 *
 * Orchestrates the workflow: overview + new update (upload → dry-run → install)
 * + history. No secrets stored client-side (reauth token stays in useUpdater ref).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import CurrentVersionCard from "../../components/updater/CurrentVersionCard";
import SystemHealthCard from "../../components/updater/SystemHealthCard";
import BackupPanel from "../../components/updater/BackupPanel";
import UploadDropzone from "../../components/updater/UploadDropzone";
import DryRunReport from "../../components/updater/DryRunReport";
import ReauthModal from "../../components/updater/ReauthModal";
import InstallProgress from "../../components/updater/InstallProgress";
import HistoryTable from "../../components/updater/HistoryTable";
import RollbackButton from "../../components/updater/RollbackButton";
import GitHubReleaseList from "../../components/updater/GitHubReleaseList";
import { useUpdater, useUpdaterStatus } from "../../hooks/useUpdater";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import { toast } from "sonner";

export default function UpdatesPage() {
  const { status, loading: statusLoading, error: statusError, refresh: refreshStatus } = useUpdaterStatus();
  const { dryRun, installWithReauth, rollbackWithReauth, getHistory, extractDetail, hasReauth, requestReauth } = useUpdater();

  const [uploadId, setUploadId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [dryRunError, setDryRunError] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);
  const [reauthContext, setReauthContext] = useState(null); // { op, ref, next }
  const [activeOp, setActiveOp] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyErr, setHistoryErr] = useState(null);
  const [source, setSource] = useState("local"); // "local" | "github"

  const loadHistory = useCallback(async () => {
    try {
      const h = await getHistory({ limit: 200 });
      setHistory(h || []);
      setHistoryErr(null);
    } catch (err) {
      setHistoryErr(extractDetail(err, "Impossible de charger l'historique."));
    }
  }, [getHistory, extractDetail]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Detect existing lock on mount (survive tab close/reopen).
  useEffect(() => {
    if (status?.lock_held_by && !activeOp) {
      // Look up latest operation_id from history
      getHistory({ limit: 20 }).then((h) => {
        const latest = (h || []).find((e) => e.operation_id && ["install_started", "rollback_started"].includes(e.event_type));
        if (latest) setActiveOp(latest.operation_id);
      }).catch(() => {});
    }
  }, [status?.lock_held_by, activeOp, getHistory]);

  const onUploaded = async (data) => {
    setUploadId(data.upload_id);
    setPlan(null);
    setDryRunError(null);
    try {
      const p = await dryRun(data.upload_id);
      setPlan(p);
    } catch (err) {
      setDryRunError(extractDetail(err, "Échec du dry-run."));
    }
  };

  const startInstall = async (uploadIdArg, typedConfirmation) => {
    if (!hasReauth()) {
      setReauthContext({ op: "install", ref: uploadIdArg, next: (typed) => performInstall(uploadIdArg, typed || typedConfirmation) });
      return;
    }
    performInstall(uploadIdArg, typedConfirmation);
  };

  const performInstall = async (uid, typedConfirmation) => {
    setInstallBusy(true);
    try {
      const before = await getHistory({ limit: 5 });
      const promise = installWithReauth(uid, typedConfirmation);
      // Grab operation_id from journal shortly after
      setTimeout(async () => {
        try {
          const h = await getHistory({ limit: 20 });
          const started = (h || []).find((e) => e.event_type === "install_started");
          if (started?.operation_id) setActiveOp(started.operation_id);
        } catch (e) { /* ignore */ }
      }, 700);
      const result = await promise;
      toast.success(`Installation terminée : ${result.version}`);
      setUploadId(null);
      setPlan(null);
      refreshStatus();
      loadHistory();
    } catch (err) {
      toast.error(extractDetail(err, "Échec de l'installation."));
    } finally {
      setInstallBusy(false);
    }
  };

  const startRollback = () => {
    if (!hasReauth()) {
      setReauthContext({ op: "rollback", ref: status?.previous_version || null, next: () => performRollback() });
      return;
    }
    performRollback();
  };

  const performRollback = async () => {
    try {
      const promise = rollbackWithReauth({ targetVersion: null });
      setTimeout(async () => {
        try {
          const h = await getHistory({ limit: 20 });
          const started = (h || []).find((e) => e.event_type === "rollback_started");
          if (started?.operation_id) setActiveOp(started.operation_id);
        } catch (e) { /* ignore */ }
      }, 700);
      const result = await promise;
      toast.success(`Rollback terminé : ${result.version}`);
      refreshStatus();
      loadHistory();
    } catch (err) {
      toast.error(extractDetail(err, "Échec du rollback."));
    }
  };

  const lockActive = Boolean(status?.lock_held_by);

  return (
    <div className="space-y-6" data-testid="updates-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-primary">Mises à jour</h1>
        <p className="text-sm text-muted-foreground">Gérer les releases ITERCATUS sur la cible de déploiement (isolée de l'app Preview).</p>
      </div>

      {activeOp && (
        <InstallProgress operationId={activeOp} onClose={() => setActiveOp(null)} />
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system">État du système</TabsTrigger>
          <TabsTrigger value="new" disabled={lockActive} data-testid="tab-new">Nouvelle mise à jour</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="space-y-4">
          <SystemHealthCard />
          <BackupPanel />
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          <CurrentVersionCard
            status={status}
            loading={statusLoading}
            error={statusError}
            onRetry={refreshStatus}
          />
          <Card className="p-6">
            <p className="text-sm text-muted-foreground mb-2">Action rapide</p>
            <RollbackButton
              previousVersion={status?.previous_version}
              disabled={lockActive || Boolean(statusError)}
              onStartRollback={startRollback}
            />
          </Card>
        </TabsContent>

        <TabsContent value="new" className="space-y-4">
          {lockActive ? (
            <Alert><AlertDescription>Une opération est déjà en cours.</AlertDescription></Alert>
          ) : (
            <>
              {!uploadId && (
                <div className="flex items-center gap-3" data-testid="update-source-switch">
                  <span className="text-sm text-muted-foreground">Source :</span>
                  <ToggleGroup type="single" value={source} onValueChange={(v) => v && setSource(v)}>
                    <ToggleGroupItem value="local" data-testid="source-local">Fichier local</ToggleGroupItem>
                    <ToggleGroupItem value="github" data-testid="source-github">Depuis GitHub</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}
              {!uploadId && source === "local" && (
                <UploadDropzone maxUploadMb={500} onUploaded={onUploaded} />
              )}
              {!uploadId && source === "github" && (
                <GitHubReleaseList
                  onFetched={(data) => onUploaded({ upload_id: data.upload_id })}
                />
              )}
              {dryRunError && <Alert variant="destructive"><AlertDescription>{dryRunError}</AlertDescription></Alert>}
              {plan && (
                <DryRunReport
                  plan={plan}
                  uploadId={uploadId}
                  onInstall={startInstall}
                  busy={installBusy}
                />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="history">
          {historyErr && <Alert variant="destructive"><AlertDescription>{historyErr}</AlertDescription></Alert>}
          <HistoryTable events={history} />
        </TabsContent>
      </Tabs>

      <ReauthModal
        open={Boolean(reauthContext)}
        op={reauthContext?.op}
        ref={reauthContext?.ref}
        onSubmit={async (password) => requestReauth({ password, op: reauthContext?.op, ref: reauthContext?.ref })}
        onSuccess={() => {
          const next = reauthContext?.next;
          setReauthContext(null);
          if (next) next();
        }}
        onCancel={() => setReauthContext(null)}
      />
    </div>
  );
}
